import { FC, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { usePrivilege } from "../../../../components/priviledge/PriviledgeProvider";
import useFetch from "../../../../hooks/useFetch";
import AsyncImg from "../../../../components/extras/AsyncImg";
import DeletePopover from "../../../../components/buttons/DeletePopover";
import { MdOutlineFileDownload } from "react-icons/md";
import { FaEye } from "react-icons/fa6";
import { Button, Empty, Text, Tooltip } from "rizzui";
import cn from "../../../../utils/classNames";
import { AddPhotosModal } from "../components/AddPhotosModal";
import useHandleDownloadDocument from "../../../../hooks/useDownloadDocument";
import { toast } from "sonner";
import useHandleErrors from "../../../../hooks/useHandleErrors";
import CarrionProfileLayout from "../CarrionProfileLayout";
import { CarrionPhotosService } from "../../../../services/carrion/carrionPhotos/carrionPhotosService";
import { DocumentsService } from "../../../../services/document/documentsService";
import Viewer from "react-viewer";
import CustomSpinner from "../../../../loader/CustomSpinner";

interface CarrionPhotosProps { }


const CarrionPhotos: FC<CarrionPhotosProps> = () => {

    // STATES

    const [isRefecth, setIsRefecth] = useState<boolean>(true);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
    const [dataFormatted, setDataFormatted] = useState<any>(null);

    // HOOKS
    
    const { id = '' } = useParams<{ id: string }>();
    const service = new CarrionPhotosService();
    const { userCan } = usePrivilege();
    const {handleDownloadDocument} = useHandleDownloadDocument()
    const { handleErrors } = useHandleErrors();


    // DATA FETCHING

    const [data, loading, error, refetchPhoto] = useFetch(useCallback(async () => {
        if (!id || id === '' || isRefecth == false) return null;
        if (isRefecth) setIsRefecth(false);

        const response = await service.getAllPhotosFromCarrion(id);
        return response.getResponseData();
    }, [id,]));


    // FUNCTIONS

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UNA FOTO
     * @EN DELETE AN PHOTO
     * @param id
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteCarrionPhoto(id)).getResponseData();
        if(response.success) {
            toast.success("Imagen eliminada correctamente");
            refetchPhoto();
        }else {
            handleErrors(response);
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES RENDER URL DE LA FOTO
     * @EN RENDER URL OF THE PHOTO
     * @param id
     * @param fileName
     * @param extension
     * @param type
     * @returns
     */
    //-----------------------------------------------------------------------------------------------------------------------------
    const renderPhoto = async (id: string, fileName: string, extension: string) => {
            const response = await new DocumentsService().renderDocument(id);
        //@ts-ignore
        const file = new Blob([response.getResponse().data], { type: 'image/' + extension });
        const formattedData = {
            fileName: fileName,
            src: URL.createObjectURL(file),
        };
        setIsViewerOpen(true);
        setDataFormatted(formattedData);
        return formattedData;   
    };

    //-----------------------------------------------------------------------------------------------------------------------------


    // RENDER

    return (
        <>
            { loading ?  <CustomSpinner /> : data?.length !== 0 ?
                <>
                        <div className="@container">
                            {/* Contenedor del botón */}
                            <div className="flex justify-start mt-1">
                                {userCan('create', 'carrion_photos') && (
                                    <Button
                                        color="primary"
                                        onClick={() => {
                                            setIsOpenModal(true);
                                        }}
                                        className={cn([
                                            'w-full',
                                            'md:w-auto',
                                            'mt-0'
                                        ])}
                                    >
                                        Añadir fotos
                                    </Button>
                                )}
                            </div>
                
                            {/* Galería de fotos */}
                            <div className="grid md:grid-cols-3 grid-cols-1 gap-4 mt-4">
                                {data?.map((photo: any) => (
                                    <div key={photo.id} className="flex flex-col relative">
                                        <AsyncImg
                                            id={photo.document.id}
                                            isBackground
                                            className="mx-auto d-block img-fluid rounded w-full h-[350px] object-cover border border-primary border-2 rounded-4 p2"
                                        />
                                        <div className="button-delete-image border-none">
                                            <DeletePopover
                                                title={`Eliminar Imagen de la Carroña`}
                                                description={`¿Estás seguro de que deseas eliminar esta imagen?`}
                                                noBorder
                                                onDelete={() => handleDelete(photo.id)}
                                            />
                                        </div>
                                        <Tooltip content={'Descargar'} placement={'top'} color={'invert'} size={'sm'}>
                                            <Button
                                                onClick={() => {
                                                    handleDownloadDocument(
                                                        new CarrionPhotosService().renderPhoto,
                                                        photo.id,
                                                        photo.fileName,
                                                        'image/' + photo.document.extension
                                                    );
                                                }}
                                                className="button-download-image"
                                            >
                                                <MdOutlineFileDownload className="h-6 w-6" color="black" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content={'Visualizar'} placement={'top'} color={'invert'} size={'sm'}>
                                            <Button onClick={() => {
                                                renderPhoto(photo.document.id, photo.fileName, photo.document.extension);
                                            }} className="button-show-image">
                                                <FaEye className="h-6 w-6 me-2" color="white" />
                                                Visualizar
                                            </Button>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {isViewerOpen && dataFormatted && (
                            <Viewer
                                visible={isViewerOpen}
                                changeable={false}
                                onClose={() => {
                                    setIsViewerOpen(false);
                                    setDataFormatted(null);
                                }}
                                images={[{ src: dataFormatted.src, alt: dataFormatted.fileName }]}
                            />
                        )}
                
                        {/* Modal para añadir fotos */}
                        <AddPhotosModal
                            isOpen={isOpenModal}
                            onClose={() => setIsOpenModal(false)}
                            refetch={refetchPhoto}
                            carrionId={id}
                        />
                </>
                :
                <>
                    <div className="py-5 text-center lg:py-8">
                        <Empty /> <Text className="mt-3">No hay Fotos</Text>

                    </div>
                    <div className="flex justify-center mt-1">
                        {userCan('create', 'carrion_photos') && (
                            <Button
                                color="primary"
                                onClick={() => {
                                    setIsOpenModal(true);
                                }}
                                className={cn([
                                    'w-full',
                                    'md:w-auto',
                                    'mt-0'
                                ])}
                            >
                                Añadir fotos
                            </Button>
                        )}
                    </div>
                    <AddPhotosModal
                        isOpen={isOpenModal}
                        onClose={() => setIsOpenModal(false)}
                        refetch={refetchPhoto}
                        carrionId={id}
                    />
                </>
            }
        </>
    );

}

export default CarrionPhotos;